import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createIntl, IntlProvider, useIntl, type IntlShape, type MessageFormatElement } from 'react-intl';
import { DEFAULT_LOCALE, LOCALE_COOKIE, PSEUDO_LOCALE, isLocale, pickLocale, type Locale } from '@platform/shared';
// Compiled by `npm run i18n:compile` (web/scripts/i18n.ts) from shared/locales/{en,fr,es}.json. English
// ships in the shell; French, Spanish and the pseudo-locale are their own chunks (planning-app.md §4.5 budget).
import en from './compiled/en.json';

/**
 * UI locale for the SPA (internationalization.md §2; planning-app i18n section). Resolution order:
 * `?lang=` in the URL (one visit), the signed-in person's `people.locale` (synced by App), the
 * `brain_locale` cookie / localStorage written by the switcher, `navigator.languages`, `en`.
 * `en-XA` is the pseudo-locale: every message accented so a hard-coded string stands out; it is
 * loaded on demand and reachable only through `?lang=en-XA` or the storage key (the CI sweep).
 */
export type UiLocale = Locale | typeof PSEUDO_LOCALE;

type Messages = Record<string, string> | Record<string, MessageFormatElement[]>;
// The pseudo-locale is pre-parsed (AST) because formatjs only accents messages in that form.
const loaded: Partial<Record<UiLocale, Messages>> = { en: en as unknown as Messages };
const STORAGE_KEY = 'planning.locale';

/** Fetches a catalog chunk once; main.tsx awaits the initial one so the first paint is already translated. */
export async function preloadLocale(locale: UiLocale): Promise<Messages> {
  const have = loaded[locale];
  if (have) return have;
  const mod = locale === PSEUDO_LOCALE ? await import('./compiled/en-XA.json') : locale === 'fr' ? await import('./compiled/fr.json') : locale === 'es' ? await import('./compiled/es.json') : null;
  const messages = (mod?.default ?? en) as Messages;
  loaded[locale] = messages;
  return messages;
}

/**
 * Namespaced catalogs (`compiled/<locale>.<ns>.json`, see scripts/i18n.ts MEMBERSHIP): a room's own
 * messages ship with the room, not in the shell, so the shell stays under the §4.5 budget.
 */
const namespaceChunks = import.meta.glob<{ default: Messages }>('./compiled/*.*.json');
const loadedNamespaces = new Map<string, Messages>();
const pendingNamespaces = new Map<string, Promise<Messages>>();

export function preloadNamespace(locale: UiLocale, ns: string): Promise<Messages> {
  const key = `${locale}/${ns}`;
  const have = loadedNamespaces.get(key);
  if (have) return Promise.resolve(have);
  let pending = pendingNamespaces.get(key);
  if (!pending) {
    const load = namespaceChunks[`./compiled/${locale}.${ns}.json`];
    pending = (load ? load().then((m) => m.default) : Promise.resolve({} as Messages)).then((messages) => {
      loadedNamespaces.set(key, messages);
      pendingNamespaces.delete(key);
      return messages;
    });
    pendingNamespaces.set(key, pending);
  }
  return pending;
}

/**
 * Wraps a lazy boundary whose messages live in a namespace (src/pages/namespaces.ts): suspends
 * until that catalog is in (the Suspense fallback stays up), then provides the shell's messages
 * plus the boundary's. Every locale has its namespace chunks, English included — production builds
 * strip the `defaultMessage` from the code (scripts/strip-message-descriptions.ts), so a message the
 * scope lacks would render as its id; `npm run i18n:check` refuses that statically.
 */
export function MessageScope({ ns, children }: { ns: string; children: ReactNode }) {
  const { locale } = useLocale();
  const intl = useIntl();
  const extra = loadedNamespaces.get(`${locale}/${ns}`);
  if (!extra) throw preloadNamespace(locale, ns);
  return (
    <IntlProvider locale={intl.locale} defaultLocale={intl.defaultLocale} messages={{ ...intl.messages, ...extra } as Messages} onError={onIntlError}>
      {children}
    </IntlProvider>
  );
}

/** The shell's intl plus a namespace's messages, for code that formats outside the tree (the report widget's labels, components/report/useReportWidget.ts). */
export async function intlWithNamespace(intl: IntlShape, locale: UiLocale, ns: string): Promise<IntlShape> {
  const extra = await preloadNamespace(locale, ns);
  return createIntl({ locale: intl.locale, defaultLocale: intl.defaultLocale, messages: { ...intl.messages, ...extra } as Messages, onError: onIntlError });
}

function onIntlError(err: { code: string }): void {
  // Missing translations are caught by `npm run i18n:check`; at runtime fall back quietly.
  if (err.code === 'MISSING_TRANSLATION') return;
  console.error(err);
}

interface LocaleContextValue {
  locale: UiLocale;
  /** Changes the locale for this browser; the caller persists it to the person when signed in. */
  setLocale(next: UiLocale): void;
}
const LocaleContext = createContext<LocaleContextValue | null>(null);

export function resolveInitialLocale(): UiLocale {
  const fromUrl = new URLSearchParams(window.location.search).get('lang');
  if (fromUrl === PSEUDO_LOCALE || isLocale(fromUrl)) return fromUrl;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === PSEUDO_LOCALE || isLocale(stored)) return stored;
  return pickLocale(navigator.languages ?? [navigator.language], DEFAULT_LOCALE);
}

export function persistLocale(locale: UiLocale): void {
  window.localStorage.setItem(STORAGE_KEY, locale);
  // Server-rendered pages (refusal, errors) read this cookie; never HttpOnly by design.
  const base = isLocale(locale) ? locale : DEFAULT_LOCALE;
  document.cookie = `${LOCALE_COOKIE}=${base}; Path=/; Max-Age=31536000; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
}

/** The BCP 47 tag Intl and `<html lang>` get: the pseudo-locale formats like English. */
export function intlLocaleFor(locale: UiLocale): string {
  return locale === PSEUDO_LOCALE ? 'en' : locale;
}

export function LocaleProvider({ children, initialLocale }: { children: ReactNode; initialLocale?: UiLocale }) {
  const [locale, setLocaleState] = useState<UiLocale>(() => initialLocale ?? resolveInitialLocale());
  const [, bump] = useState(0);

  useEffect(() => {
    document.documentElement.lang = locale === PSEUDO_LOCALE ? 'en-XA' : locale;
    document.documentElement.dir = 'ltr';
    persistLocale(locale);
  }, [locale]);

  useEffect(() => {
    if (loaded[locale]) return;
    let cancelled = false;
    preloadLocale(locale).then(() => {
      if (!cancelled) bump((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const setLocale = useCallback((next: UiLocale) => setLocaleState(next), []);
  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);
  // English until the chunk lands (only when the locale is switched at runtime; boot awaits it).
  const messages: Messages = loaded[locale] ?? (en as unknown as Messages);

  return (
    <LocaleContext.Provider value={value}>
      <IntlProvider
        locale={intlLocaleFor(locale)}
        defaultLocale={DEFAULT_LOCALE}
        messages={messages}
        onError={onIntlError}
      >
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale outside LocaleProvider');
  return ctx;
}
